import type { Map } from 'maplibre-gl';
import type { FeatureCollection, Polygon, MultiPolygon } from 'geojson';
import type { WemapMap } from '@wemap/map';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from './geoJsonSource.js';

interface VpsLevel {
    id: number;
    floorAltitude: number;
    ceilingAltitude: number;
    geometry: FeatureCollection<Polygon | MultiPolygon> | null;
}

const VPS_LEVELS_SOURCE = 'vps-levels';

export default class VpsHandler {

    private readonly map: Map;
    vpsMap: string;
    levels: VpsLevel[] = [];

    layersID = ['vps-level-lines'];

    constructor(wemapMap: WemapMap, vpsMap: string) {
        this.vpsMap = vpsMap;
        this.map = wemapMap.maplibre;

        wemapMap.addSource(VPS_LEVELS_SOURCE, {
            type: 'geojson',
            data: EMPTY_FEATURE_COLLECTION,
        });

        wemapMap.addLayer({
            id: 'vps-level-lines',
            type: 'line',
            source: VPS_LEVELS_SOURCE,
            paint: {
                'line-color': '#22C55E',
                'line-width': 2
            }
        }, { indoor: true });
    }

    get levelsEndpoint() {
        return `${this.vpsMap}/levels`;
    }

    async init() {
        try {
            const response = await fetch(this.levelsEndpoint);
            if (!response.ok) {
                throw new Error(`Failed to fetch VPS levels: ${response.status}`);
            }
            
            this.levels = await response.json();
        } catch (error) {
            console.error('Error fetching VPS levels:', error);
        }
    }

    updateLevelsDisplay() {
        if (!this.map.style) {
            return;
        }

        const allFeatures: any[] = [];
        
        this.levels.forEach((level) => {
            if (level.geometry && level.geometry.features) {
                level.geometry.features.forEach((feature) => {
                    const enhancedFeature = {
                        ...feature,
                        properties: {
                            ...feature.properties,
                            level: level.id.toString(),
                            floorAltitude: level.floorAltitude,
                            ceilingAltitude: level.ceilingAltitude
                        }
                    };
                    allFeatures.push(enhancedFeature);
                });
            }
        });

        setGeoJsonSourceData(this.map, VPS_LEVELS_SOURCE, {
            type: 'FeatureCollection',
            features: allFeatures
        });
    }

    hideLevels() {
        setGeoJsonSourceData(this.map, VPS_LEVELS_SOURCE, EMPTY_FEATURE_COLLECTION);
    }

    showLevels() {
        this.updateLevelsDisplay();
    }
}
